// useTryCloseDraft — gate any caller that wants to swap the active
// draft scratchpad (e.g. opening a different DRAFT row from the list)
// behind the same dirty-close modal that DraftDrawer renders. When
// no draft is loaded or the active scratchpad is clean, the action
// runs immediately; otherwise the action is parked in
// `pendingDraftCloseFollowUp` and the modal opens.
//
// @aiPurpose Surface a single guard for non-router exits that would
//   otherwise replace or unmount the scratchpad without prompting.

import { useCallback } from 'react';
import { canClose } from './draft-activity.js';
import { isDraftDirty, useRequestsStore } from './store.js';

export function useTryCloseDraft(): (action: () => void) => void {
  const setPendingFollowUp = useRequestsStore((s) => s.setPendingDraftCloseFollowUp);
  return useCallback(
    (action) => {
      const s = useRequestsStore.getState();
      if (s.draft === null) {
        action();
        return;
      }
      // INVARIANT (#1594): never swap/abandon the scratchpad while a
      // lifecycle action or photo upload is in flight. The resolving upload
      // key would otherwise land in the NEXT scratchpad's `unsavedPhotoKeys`
      // (or be missed by cleanup), orphaning the abandoned draft's storage
      // object. `canClose` is the same idle-and-no-upload gate DraftDrawer's
      // close path uses; the swap is dropped (the user retries the row click
      // once the upload/action settles).
      if (!canClose(s.draftActivity, s.pendingPhotoUploads)) {
        return;
      }
      if (!isDraftDirty(s.draft, s.baselineDraft)) {
        action();
        return;
      }
      // INVARIANT (#521): never run `action` synchronously when the
      // scratchpad is dirty — DraftDrawer's modal owns the
      // save/discard/cancel branch and resumes the action via
      // `pendingDraftCloseFollowUp` exactly once.
      setPendingFollowUp(() => action);
    },
    [setPendingFollowUp],
  );
}
